import React from 'react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { Separator } from '@cotera/client/v0/components/ui/separator';
import { humanReadableSchedule } from '@cotera/client/app/v1/trigger/schedule-utils';
import {
  PayloadBlock,
  Section,
  StepError,
  StepHeader,
} from './run-step-view-sections';
import {
  describeFlushReason,
  readBucketFlushEnvelope,
} from './bucket-flush-envelope';
import type { WorkflowRunStepView, WorkflowRunStepViewProps } from './types';

const { WorkflowNodeType, WorkflowRunStepStatus, parseBucketConfig } =
  PersistedWorkflow;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const formatMoment = (iso: string | null): string => {
  if (iso === null) {
    return '—';
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const plural = (n: number, word: string): string =>
  `${n} ${word}${n === 1 ? '' : 's'}`;

/** One reducer output, named the way the flush envelope keys it. */
const ReducedRow: React.FC<{ name: string; value: unknown }> = ({
  name,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-4 py-1">
    <code className="text-muted-foreground text-xs">reduced.{name}</code>
    <span className="text-foreground font-mono text-sm">
      {typeof value === 'object' && value !== null
        ? JSON.stringify(value)
        : String(value)}
    </span>
  </div>
);

/**
 * Step view for a bucket node, built around the thing the run list cannot say:
 * a bucket's story is spread over many runs, and any one run only shows its own
 * end of it.
 *
 * Two different steps land here and they mean opposite things.
 *
 * An **absorbed** step is this run's contribution being collected. Nothing is
 * wrong and nothing is finished — the branch below it runs later, in a flush
 * run. The default JSON view shows identical input and output (the node stores
 * what it was given) under a grey badge that reads like a failure, which is the
 * same trap the debounce view was written to get out of.
 *
 * A **completed** step is the flush itself, and its payload is the envelope —
 * `items`, `count`, `reduced`, `window` — which is the only place the size and
 * the cause of a flush are recorded. As raw JSON that is a wall with the answer
 * buried in it, so this reads it out: how many items, why it fired, and over
 * what span they were collected.
 */
export const BucketRunStepView: React.FC<WorkflowRunStepViewProps> = ({
  node,
  payload,
}) => {
  const parsed = parseBucketConfig(isRecord(node.data) ? node.data : {});
  const recordedInput = payload?.input ?? null;
  const recordedOutput = payload?.output ?? null;

  const absorbed = node.status === WorkflowRunStepStatus.Absorbed;
  // Read off the payload rather than the status: the flush step is seeded
  // `Completed`, which on its own does not distinguish a flush from anything
  // else, whereas only a flush carries an envelope.
  const flush =
    readBucketFlushEnvelope(recordedOutput) ??
    readBucketFlushEnvelope(recordedInput);

  return (
    <div className="bg-background flex h-full flex-col gap-6 overflow-y-auto px-6 py-6">
      <StepHeader name={node.name} status={node.status} />

      {node.error !== null && node.error !== '' ? (
        <Section title="Error">
          <StepError error={node.error} />
        </Section>
      ) : null}

      <Section title={flush !== null ? 'This flush' : 'Bucket'}>
        <div className="flex flex-col gap-2">
          {flush !== null ? (
            <>
              <Text.P className="text-foreground">
                Flushed {describeFlushReason(flush.reason)}, with{' '}
                {plural(flush.count, 'item')}.
              </Text.P>
              <Text.Caption className="text-muted-foreground block">
                {flush.from === null
                  ? `Flushed ${formatMoment(flush.flushedAt)}.`
                  : `Collected between ${formatMoment(
                      flush.from
                    )} and ${formatMoment(flush.to)}.`}
              </Text.Caption>
            </>
          ) : absorbed ? (
            <>
              <Text.P className="text-foreground">
                Collected and held. This run&apos;s branch ends here.
              </Text.P>
              <Text.Caption className="text-muted-foreground block">
                Nothing failed. The steps below the bucket run once later, in a
                flush run, with this run&apos;s output and every other one the
                bucket is holding.
              </Text.Caption>
            </>
          ) : null}
          {parsed.ok ? (
            <Text.Caption className="text-muted-foreground block">
              {describePolicy(parsed.config.policy)}
            </Text.Caption>
          ) : (
            <Text.Caption className="text-muted-foreground block">
              {parsed.error}
            </Text.Caption>
          )}
          {/* Last, and muted: the steps above a bucket are pruned in a flush run
              — it exists to run the branch below — and render as bare "Stopped"
              cards. Said here because the graph has nowhere to say it, but it
              answers a question asked only after the ones above. */}
          {flush !== null ? (
            <Text.Caption className="text-muted-foreground/80 block">
              This run exists to run the branch below the bucket. Steps above it
              show as stopped because they did not need to run again — they
              already ran, once per collected item.
            </Text.Caption>
          ) : null}
        </div>
      </Section>

      {flush !== null && flush.reduced.length > 0 ? (
        <Section title="Worked out across the batch">
          <div className="border-input divide-border divide-y rounded-md border px-3 py-1">
            {flush.reduced.map((entry) => (
              <ReducedRow
                key={entry.name}
                name={entry.name}
                value={entry.value}
              />
            ))}
          </div>
        </Section>
      ) : null}

      <Separator />

      <Section title={flush !== null ? `Items (${flush.count})` : 'Collected'}>
        <PayloadBlock
          value={flush !== null ? flush.items : recordedOutput}
          emptyText={
            absorbed
              ? 'Nothing was recorded for this contribution.'
              : 'No output recorded for this step.'
          }
        />
      </Section>
    </div>
  );
};

/** "Flushes every 100 items, or daily at 9:00 AM." */
function describePolicy(policy: PersistedWorkflow.BucketFlushPolicy): string {
  const parts = policy.triggers.map((trigger) =>
    trigger.kind === 'count'
      ? `every ${plural(trigger.threshold, 'item')}`
      : humanReadableSchedule(trigger.cron).toLowerCase()
  );
  const unique = [...new Set(parts)];
  return unique.length === 0
    ? 'No flush policy configured.'
    : `Flushes ${unique.join(' or ')}.`;
}

export const bucketRunStepView: WorkflowRunStepView = {
  key: 'bucket',
  matches: (nodeType) => nodeType === WorkflowNodeType.Bucket,
  needsPayload: true,
  render: (props) => <BucketRunStepView {...props} />,
};
